import { Component, EventEmitter, Input, Output } from "@angular/core";

export interface MultiSelectChipOption {
	label: string;
	value: any;
	selected: boolean;
}

@Component({
	selector: "app-multi-select-chips",
	templateUrl: "./multi-select-chips.component.html",
	styleUrls: ["./multi-select-chips.component.scss"],
})
export class MultiSelectChipsComponent {
	@Input() options: MultiSelectChipOption[] = [];
	@Input() selectWidth = "";
	@Input() placeholder = "Select Option";
	@Output() added = new EventEmitter<MultiSelectChipOption>();
	@Output() removed = new EventEmitter<MultiSelectChipOption>();
	@Input() disabled = false;

	@Input() defaultChips = [];

	SELECT_INSPECTIONS_VALUE = "-none-";
	// keep string-based model
	selectedValue: string = this.SELECT_INSPECTIONS_VALUE;

	// used to force-recreate the <select> when returning to placeholder
	showSelect = true;

	get unselectedOptions() {
		return this.options.filter(o => !o.selected);
	}

	get selectedOptions() {
		return this.options.filter(o => o.selected);
	}

	// Normalize to the exact string the <option [value]> will produce
	private toSelectString(v: any): string {
		if (Array.isArray(v)) return v.map(x => String(x ?? "")).join(",");
		return v != null ? String(v) : "";
	}

	private isNone(v: any) {
		return v === this.SELECT_INSPECTIONS_VALUE || v === "" || v == null;
	}

	private refreshSelectForPlaceholder() {
		// Force a fresh <select> so the browser cannot preserve selectedIndex
		this.showSelect = false;
		// next macrotask tick is enough to recreate element
		setTimeout(() => (this.showSelect = true));
	}

	onSelect() {
		if (this.isNone(this.selectedValue)) return;

		const selStr = String(this.selectedValue); // always a string from [value]
		this.options = this.options.map(o => {
			if (this.toSelectString(o.value) === selStr) {
				const next = { ...o, selected: true };
				this.added.emit(next);
				return next;
			}
			return o;
		});

		// reset to placeholder and force immediate placeholder render
		this.selectedValue = this.SELECT_INSPECTIONS_VALUE;
		this.refreshSelectForPlaceholder();
	}

	removeChip(option: MultiSelectChipOption) {
		const optStr = this.toSelectString(option.value);

		this.options = this.options.map(o => (this.toSelectString(o.value) === optStr ? { ...o, selected: false } : o));

		this.removed.emit({ ...option, selected: false });
		this.selectedValue = this.SELECT_INSPECTIONS_VALUE;

		this.defaultChips = this.defaultChips.filter(o => o.value !== option.value);

		// ensure placeholder shows *immediately* when last chip is removed
		this.refreshSelectForPlaceholder();
	}
}
